import type * as React from "react";

import { cn } from "../lib/cn";

/** `sheet` for a whole card, or on grounds `bg-recess` can't be seen against (manila, cork). */
export type SkeletonShape = "bar" | "sheet";

const shapeSurface: Record<SkeletonShape, string> = {
  bar: "bg-recess",
  sheet: "border border-rule bg-card shadow-lift",
};

const STAGGER_MS = 80;

export interface SkeletonProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  shape?: SkeletonShape;
  /** Place in a stack; each step delays the pulse. */
  index?: number;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/** Size with `className`; aria-hidden, so put `aria-busy` on the loading region. */
export function Skeleton({
  shape = "bar",
  index = 0,
  className,
  style,
  ...rest
}: SkeletonProps) {
  const delay = index * STAGGER_MS;

  return (
    <div
      aria-hidden="true"
      {...rest}
      className={cn(
        "animate-pulse rounded-paper motion-reduce:animate-none",
        shapeSurface[shape],
        className,
      )}
      style={delay ? { animationDelay: `${delay}ms`, ...style } : style}
    />
  );
}

export interface SkeletonStackProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  count: number;
  shape?: SkeletonShape;
  itemClassName?: string;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

export function SkeletonStack({
  count,
  shape,
  itemClassName,
  className,
  ...rest
}: SkeletonStackProps) {
  return (
    <div
      aria-hidden="true"
      {...rest}
      className={cn("flex flex-col gap-2", className)}
    >
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} shape={shape} index={i} className={itemClassName} />
      ))}
    </div>
  );
}
